/**
 * useContextRebuild — tracks whether foundation panel data has changed
 * since the writer last explicitly rebuilt the AI context.
 *
 * Returns:
 *  hasPending  — true when characters, variables or plot have changed
 *                since the last rebuild()
 *  rebuild()   — call to lock in the current data as the "built" baseline;
 *                dispatches loom:context-rebuilt for any AI subsystems listening
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePW } from '@/lib/store';

function buildKey(projectId) {
  return `cr_ctx_built_${projectId}`;
}

function makeFingerprint({ project, characters, variables }) {
  if (!project?.id) return null;
  return JSON.stringify({
    charCount:     (characters || []).length,
    charIds:       (characters || []).map((c) => c.id || c._id).sort().join(','),
    varKeys:       Object.keys(variables || {}).sort().join(','),
    plotExtracted: !!(project?.plot_extraction?.extracted_at),
    plotStructure: project?.plot_extraction?.structure_type || null,
  });
}

export function useContextRebuild() {
  const { project, characters = [], variables = {} } = usePW();
  const projectId = project?.id;

  // Current foundation fingerprint
  const fingerprint = useMemo(
    () => makeFingerprint({ project, characters, variables }),
    [project, characters, variables],
  );

  // Last-built fingerprint persisted in localStorage
  const [builtFingerprint, setBuiltFingerprint] = useState(() => {
    if (!projectId) return null;
    try { return localStorage.getItem(buildKey(projectId)); } catch { return null; }
  });

  // Re-read from storage when project switches
  useEffect(() => {
    if (!projectId) return;
    try { setBuiltFingerprint(localStorage.getItem(buildKey(projectId))); } catch { /* ok */ }
  }, [projectId]);

  // Pending = fingerprint exists AND differs from what was last built
  const hasPending = !!(fingerprint && fingerprint !== builtFingerprint);

  // Per-panel pending flags — which specific panels changed since last rebuild.
  const pendingByPanel = useMemo(() => {
    if (!fingerprint || !builtFingerprint || fingerprint === builtFingerprint) return {};
    let current = null;
    let built = null;
    try {
      current = JSON.parse(fingerprint);
      built   = JSON.parse(builtFingerprint);
    } catch { return {}; }
    const pending = {};
    if (current.charCount !== built.charCount || current.charIds !== built.charIds) {
      pending.bible = true;
    }
    if (current.varKeys !== built.varKeys) {
      pending.variables = true;
    }
    if (current.plotExtracted !== built.plotExtracted || current.plotStructure !== built.plotStructure) {
      pending.plot = true;
    }
    return pending;
  }, [fingerprint, builtFingerprint]);

  const rebuild = useCallback(() => {
    if (!projectId || !fingerprint) return;
    try { localStorage.setItem(buildKey(projectId), fingerprint); } catch { /* ok */ }
    setBuiltFingerprint(fingerprint);
    window.dispatchEvent(new CustomEvent('loom:context-rebuilt', {
      detail: { projectId, fingerprint },
    }));
  }, [projectId, fingerprint]);

  return { hasPending, pendingByPanel, rebuild };
}
